<template>
    <section class="airport-tabs airport-page-section" data-test="new-l4-airport-tabs">
        <div class="airport-tabs__container">
            <h2 class="airport-tabs__title airport-page-title">{{ blok.title }}</h2>
            <RichText class="airport-tabs__descr" :richtext="blok.description" />
            <div class="airport-tabs__tabs">
                <div class="tabs-controls">
                    <button
                        class="tabs-controls__btn tabs-controls__btn--left"
                        :class="{ 'is-active': !tabsControlsCentered && !isAtLeftMargin }"
                        aria-label="Scroll to the beginning of the list"
                        @click="scrollTab(-500)"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="49" viewBox="0 0 48 49" fill="none">
                            <rect
                                width="48"
                                height="48"
                                transform="translate(48 48.7695) rotate(180)"
                                fill="white"
                                fill-opacity="0.88"
                            />
                            <path
                                d="M29 20.6629L22.8933 26.7695L29 32.8895L27.12 34.7695L19.12 26.7695L27.12 18.7695L29 20.6629Z"
                                fill="#180B1E"
                            />
                        </svg>
                    </button>
                    <div
                        ref="tabsContainer"
                        class="tabs-controls__list"
                        :class="{ 'justify-center md:justify-start': tabsControlsCentered }"
                        @scroll="showArrows"
                    >
                        <h3
                            v-for="tab in blok.tabs"
                            :key="tab._uid"
                            class="tabs-controls__item"
                            :class="{ 'is-active': currentSelected === tab._uid }"
                            :data-text="tab.tabTitle"
                            @click="toggleItem(tab._uid)"
                        >
                            {{ tab.tabTitle }}
                        </h3>
                    </div>
                    <button
                        class="tabs-controls__btn tabs-controls__btn--right"
                        :class="{ 'is-active': !tabsControlsCentered && !isAtRightMargin }"
                        aria-label="Scroll to the end of the list"
                        @click="scrollTab(500)"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="49" viewBox="0 0 48 49" fill="none">
                            <rect
                                width="48"
                                height="48"
                                transform="translate(0 0.769531)"
                                fill="white"
                                fill-opacity="0.88"
                            />
                            <path
                                d="M19 28.8762L25.1067 22.7695L19 16.6495L20.88 14.7695L28.88 22.7695L20.88 30.7695L19 28.8762Z"
                                fill="#180B1E"
                            />
                        </svg>
                    </button>
                </div>
                <div
                    v-for="tab in tabsContent"
                    :key="tab.uid"
                    class="tab-content"
                    :class="{
                        'is-active': currentSelected === tab.uid,
                        'is-tab-content-open': showFullContent,
                        'is-with-toggle': tab.withShowMoreBtn,
                    }"
                >
                    <template v-if="isRichTextNode(tab.tabContent)">
                        <RichText class="content" :richtext="tab.tabContent" />
                    </template>
                    <template v-else>
                        <component
                            :is="blok.component"
                            v-for="blok in extractBodyFromTab(tab.tabContent)"
                            :key="blok._uid"
                            :blok="blok"
                        />
                    </template>
                    <div v-if="tab.tabImage.filename" class="image">
                        <NuxtImg
                            v-if="blok.tabImage?.filename"
                            :src="blok.tabImage?.filename"
                            :title="blok?.tabImage.title"
                            :alt="blok?.tabImage.alt"
                            :width="360"
                            :height="240"
                            provider="storyblok"
                            format="webp"
                            loading="lazy"
                        />
                    </div>
                    <div v-if="tab.withShowMoreBtn" class="tab-content__btn">
                        <ShowMoreBtn
                            :is-opened="showFullContent"
                            :show-text="t('read_more')"
                            :hide-text="t('hide_more')"
                            @click="toggleVisibility"
                        />
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import type { IL4AirportTabsConfig, IL4TabBlok, IL4TabDocNode } from '~/types/components/L4AirportTabs';
import { useThrottleFn } from '@vueuse/core';
const { t } = useI18n();
const { blok } = defineProps<{ blok: IL4AirportTabsConfig }>();

const currentSelected = ref(0);

const tabsContainerWidth = ref(0);
const tabsControlsCentered = ref(false);
const isAtLeftMargin = ref(true);
const isAtRightMargin = ref(false);
const tabsContainer = ref<HTMLElement | null>(null);
const showFullContent = ref(false);
const MOBILE_GAP = 24;

const tabsContent = computed(() => {
    return blok.tabs.map((tab) => {
        const textLength = renderRichText(tab.tabContent).replace(/<[^>]*>/g, '').length;
        return {
            uid: tab._uid,
            tabContent: tab.tabContent,
            tabImage: tab.tabImage,
            withShowMoreBtn: textLength > 340,
        };
    });
});

function toggleItem(index) {
    currentSelected.value = index;
    showFullContent.value = false;
}

function scrollTab(pixels) {
    const el = tabsContainer.value;
    if (el) {
        el.scrollTo({
            left: pixels,
            behavior: 'smooth',
        });
    }
}

const showArrows = useThrottleFn((e) => {
    const container = e.target;
    const scrollPosition = container.scrollLeft;
    const scrollWidth = container.scrollWidth;
    isAtRightMargin.value = scrollPosition >= scrollWidth - tabsContainerWidth.value - 20;
    isAtLeftMargin.value = scrollPosition <= 20;
}, 30);

function toggleVisibility() {
    showFullContent.value = !showFullContent.value;
}

// Checks whether the given node is a rich text node
const isRichTextNode = (node: unknown): node is IL4TabDocNode => {
    return (
        typeof node === 'object' &&
        node !== null &&
        (node as IL4TabDocNode).type === 'doc' &&
        Array.isArray((node as IL4TabDocNode).content) &&
        !(node as IL4TabDocNode).content.some((el) => el.type === 'blok')
    );
};

// Checks whether the given node is a Storyblok doc with blok body content
const isBlokBodyNode = (node: unknown): node is IL4TabDocNode => {
    return (
        typeof node === 'object' &&
        node !== null &&
        (node as IL4TabDocNode).type === 'doc' &&
        Array.isArray((node as IL4TabDocNode).content) &&
        (node as IL4TabDocNode).content.some((el) => el.type === 'blok')
    );
};

// Extracts blok body from a Storyblok doc if present, otherwise returns empty array
const extractBodyFromTab = (node: unknown): IL4TabBlok[] => {
    if (!isBlokBodyNode(node)) return [];

    return (node as IL4TabDocNode).content.filter((n) => n.type === 'blok').flatMap((n) => n.attrs?.body ?? []);
};

onMounted(() => {
    currentSelected.value = blok.tabs?.[0]?._uid;
    nextTick(() => {
        if (!tabsContainer.value) return;
        const tabsControlsItems = [...tabsContainer.value.children];
        const tabsControlsItemWidth =
            tabsControlsItems.reduce((width, el) => width + el.getBoundingClientRect().width, 0) +
            (tabsControlsItems.length - 1) * MOBILE_GAP;
        tabsContainerWidth.value = tabsContainer.value.getBoundingClientRect().width;
        tabsControlsCentered.value = tabsControlsItemWidth <= tabsContainerWidth.value;
    });
});
</script>

<style lang="postcss" scoped>
.airport-tabs {
    &__container {
        @apply md:container;
        > :deep(.rich-text) p {
            @apply md:text-xl;
        }
    }
    &__title {
        @apply mb-3;
    }
    &__descr {
        @apply mb-8 md:text-xl;
    }
}

.tabs-controls {
    @apply relative;
    &__list {
        @apply flex items-end gap-6 md:gap-8 mb-8 border-b border-gray-200 whitespace-nowrap overflow-scroll;
        -ms-overflow-style: none; /* IE and Edge */
        scrollbar-width: none; /* Firefox */
        &::-webkit-scrollbar {
            display: none;
        }
    }
    &__item {
        @apply py-1 border-b-4 border-transparent cursor-pointer flex flex-col text-base md:text-xl;
        &:after {
            content: attr(data-text) / '';
            @apply h-0 overflow-hidden select-none pointer-events-none font-bold invisible;
            @media speech {
                @apply hidden;
            }
        }
        &.is-active {
            @apply border-primary font-bold;
        }
    }
    &__btn {
        @apply h-12 w-12 md:hidden absolute top-1/2 -translate-y-1/2 opacity-0 pointer-events-none;
        &--left {
            @apply -left-4;
        }
        &--right {
            @apply -right-4;
        }
        &.is-active {
            @apply opacity-100 pointer-events-auto;
        }
    }
}

.tab-content {
    @apply hidden flex-col md:flex-row;
    &.is-tab-content-open {
        .content {
            @apply h-auto overflow-auto;
        }
        &.is-with-toggle .content:before {
            display: none;
        }
    }
    &.is-with-toggle {
        .content:before {
            @apply md:hidden absolute bottom-0 left-0 right-0;
            content: '';
            height: 68px;
            background: linear-gradient(180deg, rgba(255, 255, 255, 0) 46.35%, #fff 100%);
        }
    }
    .content {
        @apply h-40 md:h-full md:block md:flex-1 overflow-hidden relative;
        transition:
            max-height 0.2s,
            padding 0.2s,
            margin 0.2s;
        :deep(hr) {
            @apply py-3 border-transparent;
        }
        :deep(p:not(:last-child)) {
            @apply mb-4;
        }
        :deep(a) {
            @apply text-[#0094FF] hover:underline hover:text-[#0084E3];
        }
    }
    .image {
        @apply hidden md:block mt-8 md:mt-0 md:ml-8;
        img {
            @apply max-w-[300px] md:max-w-[360px] rounded-lg mx-auto;
        }
    }

    &.is-active {
        @apply flex;
    }
    &__btn {
        @apply mt-6 md:hidden text-center;
    }
}
</style>
